/**
 * The catalogue as an Excel workbook (docs/01-architektur.md, Excel-Mappe): written and read in the
 * browser, as KellerLog does (Import.tsx there). The rules are in packages/core/src/catalog-import.ts.
 */
import { SHEET_GUIDE, guideRows } from "@speisekarte/core";
import type { Cell, Sheet } from "@speisekarte/core";

const WIDE = ["Name", "Beschreibung", "Bezeichnung", "Kategorie", "Tags", "Weitere Preise", "Überschrift"];

/** An Excel file with the sheets and the guide after them. */
export async function writeWorkbook(sheets: Sheet[], fileName: string) {
  const { default: writeXlsxFile } = await import("write-excel-file/browser");
  const cell = (c: Cell, head: boolean) => (c === null || c === undefined || c === "" ? null : head ? { value: String(c), fontWeight: "bold" as const } : { value: c instanceof Date ? c.toLocaleDateString("de-DE") : c });
  const guide = guideRows().map((r, i) => r.map((c) => (c === "" ? null : { value: c, wrap: true, ...(i === 0 ? { fontWeight: "bold" as const } : {}) })));
  await writeXlsxFile(
    [
      ...sheets.map((sh) => ({
        data: sh.rows.map((r, i) => r.map((c) => cell(c, i === 0))),
        sheet: sh.name,
        columns: (sh.rows[0] ?? []).map((h) => ({ width: String(h) === "Beschreibung" || String(h) === "Text" ? 60 : WIDE.includes(String(h)) ? 30 : 12 })),
        stickyRowsCount: 1,
      })),
      { data: guide, sheet: SHEET_GUIDE, columns: [14, 14, 8, 34, 24, 70].map((width) => ({ width })), stickyRowsCount: 1 },
    ] as never,
  ).toFile(fileName);
}

/** The sheets of an uploaded file (.xlsx; .csv as one sheet). */
export async function readWorkbook(file: File): Promise<Sheet[]> {
  if (/\.(csv|txt)$/i.test(file.name)) return [{ name: "", rows: readCsv(await file.text()) }];
  const { default: readXlsxFile } = await import("read-excel-file/browser");
  return (await readXlsxFile(file)).map((s) => ({ name: s.sheet, rows: s.data as Cell[][] }));
}

/** JSON can carry these: a date becomes its text, nothing else changes. */
export const plainSheets = (sheets: Sheet[]): Sheet[] => sheets.map((s) => ({ name: s.name, rows: s.rows.map((r) => r.map((c) => (c instanceof Date ? c.toLocaleDateString("de-DE") : c === undefined ? null : c))) }));

/** A small CSV reader: semicolon or comma, quotes as Excel writes them (from KellerLog). */
export function readCsv(text: string): Cell[][] {
  const first = text.split(/\r?\n/, 1)[0] ?? "";
  const sep = (first.match(/;/g)?.length ?? 0) >= (first.match(/,/g)?.length ?? 0) ? ";" : ",";
  const rows: Cell[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') [cell, i] = [cell + '"', i + 1];
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === sep) [row, cell] = [[...row, cell], ""];
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      rows.push([...row, cell]);
      [row, cell] = [[], ""];
    } else cell += ch;
  }
  if (cell !== "" || row.length) rows.push([...row, cell]);
  return rows;
}
